import { type SEOHandle } from '@nasa-gcn/remix-seo';
import { useLoaderData } from '@remix-run/react';
import {
  type HeadersFunction,
  type LinksFunction,
  type LoaderFunction,
  json } from
'@remix-run/server-runtime';
import { getMDXComponent } from 'mdx-bundler/client/index.js';
import * as React from 'react';
import styles from '#app/styles/mdx.css';
import { getBlogMdxListItems, getMdxPage, mdxPageMeta } from '#app/utils/mdx.ts';
import { invariant } from '#app/utils/misc.tsx';
import { slugify } from '#app/utils/text.ts';
import { getServerTimeHeader } from '#app/utils/timing.server.ts';
import { type MdxComponent } from '#types/index.ts';

export const links: LinksFunction = () => [{ rel: 'stylesheet', href: styles }];

export const handle: SEOHandle = {
  getSitemapEntries: async (request) => {
    try {
      const pages = await getBlogMdxListItems({ request });
      return pages.map((page) => {
        return { route: `/blog/${page.slug}`, priority: 0.7 };
      });
    } catch (e) {
      console.error(e);
      return [];
    }
  }
};

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return {
    'cache-control':
    loaderHeaders.get('cache-control') ?? 'private, max-age=60',
    Vary: 'Cookie'
  };
};

export const meta = mdxPageMeta;

export const loader: LoaderFunction = async ({ params, request }) => {
  const slug = params.slug;
  invariant(typeof slug === 'string', 'Slug should be a string, and defined');

  const timings = {};
  const mdxPage = await getMdxPage(
    { contentDir: 'blog', slug },
    { request, timings }
  );

  if (!mdxPage) {
    throw json(null, { status: 404 });
  }

  return json(
    { page: mdxPage },
    {
      headers: {
        'cache-control': 'private, max-age: 60',
        Vary: 'Cookie',
        'Server-Timing': getServerTimeHeader(timings)
      }
    }
  );
};

export default function Blog() {
  const data = useLoaderData<{page: MdxComponent;}>();
  let { page } = data;

  const Component = React.useMemo(() => getMDXComponent(page.code), [page]);

  return (
    <div className="noise-bg pb-8">
			<Header
        title={page.frontmatter.title ?? ''}
        imageUrl={page.frontmatter.bannerUrl} />

			{page.links && page.links.length > 0 &&
      <>
					<div className="mx-auto max-w-3xl px-8">
						<SummaryLinks items={page.links} />
					</div>
				</>}

				<hr className="mt-8 max-w-screen-lg mx-auto" />
			<article className="mdx mx-auto min-h-[100vh] px-8 pt-8 text-foreground/90 md:px-4">
				<div className="mx-auto max-w-3xl">
					<Component />
				</div>
			</article>
		</div>);

}

interface HeaderProps {
  title: string;
  imageUrl?: string;
}

const Header: React.FC<HeaderProps> = ({ title, imageUrl }) => {
  return (
    <header className="mdx relative mx-auto w-full max-w-screen-lg px-4 pb-4 pt-16">
			<div className="relative w-full">
				<div className="z-20 flex h-full w-full flex-col items-center justify-center pb-8">
					<h1
            id={slugify(title)}
            className="text-4xl font-extrabold text-foreground">

						{title}
					</h1>
				</div>
			</div>
			<div className="relative flex aspect-video h-full w-full items-center justify-center overflow-hidden sm:rounded-lg">
				<img
          loading="lazy"
          className="h-full w-full object-cover object-top md:rounded-lg"
          src={imageUrl || '/img/blogs/default.jpeg'}
          alt={'Banner for :' + title} />

			</div>
		</header>);

};

type SummaryLinksProps = {
  items: {slug: string;text: string;}[];
};

export const SummaryLinks: React.FC<SummaryLinksProps> = ({ items }) => {
  return (
    <nav className="backdrop-brightness-20 rounded-lg border-solid border-foreground/20 bg-transparent pt-8">
			<h2 className="mb-4 text-2xl font-extrabold">Summary</h2>
			<ul>
				{items.map((item, index) =>
        <li key={index} className="py-1 text-lg">
						<a className="text-blue-600" href={'#' + item.slug}>
							{item.text}
						</a>
					</li>
        )}
			</ul>
		</nav>);

};